/**
 * AOP 2027 Planning — the iteration bar (David, Oct 19 2026).
 *
 * Which iteration is open, and on a department tab where that department stands: not submitted,
 * submitted by its head, withdrawn (by the head, or because a draft changed afterwards), returned
 * or approved and locked. Anna and David get "Open iteration"; the department's head gets Submit /
 * Withdraw, and so do Anna and David on every department, in the head's place (Oct 29 2026). Every
 * rule is the proxy's (`proxy-server/lib/aopIterations.js`) — the buttons only appear where it
 * would allow them, and its refusal is shown word for word if it does not.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  AOP_TIMELINE_QUERY_KEY,
  type AopIterationStatus,
  openAopIteration,
  submitAopDepartment,
  withdrawAopDepartment,
} from '../../services/aopPlanningApi';
import { AopReviewRow } from './AopReviewRow';
import { AopSyncRow } from './AopSyncRow';
import { day, isFinalIteration, personName, who } from './aopFormat';

const MAX_NAME = 80;


type DepartmentEntry = AopIterationStatus['departments'][string] | undefined;

/** The chip and sentence for a department's state in the open iteration. */
function describeState(entry: DepartmentEntry): { variant: 'grey' | 'blue' | 'orange' | 'green'; chip: string; line: string } {
  if (!entry) return { variant: 'grey', chip: 'Not submitted', line: 'The department head submits it when the plan is ready.' };
  if (entry.state === 'submitted' && entry.stale) {
    return {
      variant: 'orange',
      chip: 'Changed since submitted',
      line: `The drafts no longer match what ${who(entry.submittedBy)} submitted on ${day(entry.submittedAt)}. Withdraw and submit again.`,
    };
  }
  if (entry.state === 'submitted') {
    return {
      variant: 'blue',
      chip: 'Submitted',
      line: `Submitted by ${who(entry.submittedBy)}${entry.submittedOnBehalf ? " in the head's place" : ''} on ${day(entry.submittedAt)} with ${entry.drafts} ${entry.drafts === 1 ? 'draft' : 'drafts'}. Any change to a draft here withdraws it.`,
    };
  }
  if (entry.state === 'withdrawn') {
    return {
      variant: 'orange',
      chip: 'Withdrawn',
      line:
        entry.withdrawReason === 'edited'
          ? `A draft changed after submitting (${who(entry.withdrawnBy)}), so it needs submitting again.`
          : `Withdrawn by ${who(entry.withdrawnBy)}${entry.withdrawReason === 'on_behalf' ? " in the head's place" : ''}.`,
    };
  }
  if (entry.state === 'returned') {
    return {
      variant: 'orange',
      chip: 'Returned',
      line: entry.returnNote
        ? `Reopened by ${who(entry.returnedBy)}: “${entry.returnNote}” Change it and submit again.`
        : `Reopened by ${who(entry.returnedBy)} — change it and submit again.`,
    };
  }
  if (entry.stale) {
    return {
      variant: 'orange',
      chip: 'Changed since approved',
      line: 'A draft changed after the approval. Anna can reopen the department to have it resubmitted.',
    };
  }
  return {
    variant: 'green',
    chip: 'Approved · locked',
    line: `Approved by ${who(entry.approvedBy)}${entry.approvedAt ? ` on ${day(entry.approvedAt)}` : ''}. Locked until the next iteration.`,
  };
}

/** The bar above the tabs' content — see the module note. */
export function AopIterationBar({ status, department }: { status: AopIterationStatus; department: string | null }) {
  const queryClient = useQueryClient();
  const [opening, setOpening] = useState(false);
  /** What opening the iteration did with the roles set aside earlier — said once, until dismissed. */
  const [openedNotice, setOpenedNotice] = useState<string | null>(null);
  /** Refetch the page, so the new iteration shows. */
  const refresh = () => void queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
  /** After opening: refetch, and say which set-aside roles came back (and which did not). */
  const onOpened = (result: Awaited<ReturnType<typeof openAopIteration>>) => {
    refresh();
    setOpenedNotice(openedText(result));
  };
  const { current } = status;

  return (
    <div className="space-y-2 rounded-md border border-zinc-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-zinc-700">
          {current ? (
            <>
              <span className="font-semibold text-zinc-900">{current.name}</span>{' '}
              {`is open · opened by ${who(current.openedBy)} on ${day(current.openedAt)}`}
            </>
          ) : (
            'No iteration is open yet — drafts can be added, and departments submit once Anna opens one.'
          )}
        </p>
        {status.canOpen && (
          <Button type="button" size="sm" variant="outline" onClick={() => setOpening(true)}>
            Open {current ? 'next' : 'an'} iteration
          </Button>
        )}
      </div>

      {openedNotice && (
        <p role="status" className="flex items-start gap-2 text-zinc-600">
          <span className="flex-1">{openedNotice}</span>
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpenedNotice(null)}>
            Dismiss
          </Button>
        </p>
      )}

      {department && current && <AopDepartmentStatus status={status} department={department} />}
      {department && current && <RevisitList entries={status.revisit?.[department] ?? []} />}

      {opening && <OpenIterationModal onClose={() => setOpening(false)} onOpened={onOpened} hasCurrent={Boolean(current)} />}
    </div>
  );
}

/**
 * One department in the open iteration: its state chip and line, Submit/Withdraw (its head, or Anna
 * and David in the head's place), the review row and the sync row. Under the iteration bar on a
 * department tab, and once per department on the Approvals tab.
 */
export function AopDepartmentStatus({ status, department }: { status: AopIterationStatus; department: string }) {
  const queryClient = useQueryClient();
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const act = useMutation({
    mutationFn: (kind: 'submit' | 'withdraw') => (kind === 'submit' ? submitAopDepartment(department) : withdrawAopDepartment(department)),
    onSuccess: () => {
      setConfirmSubmit(false);
      void queryClient.invalidateQueries({ queryKey: AOP_TIMELINE_QUERY_KEY });
    },
  });

  const { current } = status;
  const entry = status.departments[department];
  const state = describeState(entry);
  const isHead = Boolean(current && status.headOf.includes(department));
  // Anna and David act for any head; the proxy records it as on behalf.
  const forHead = !isHead && Boolean(current && status.canActForHeads);
  const canSubmit = (isHead || forHead) && entry?.state !== 'submitted' && entry?.state !== 'approved';
  const canWithdraw = (isHead || forHead) && entry?.state === 'submitted';
  if (!current) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-2">
        <Badge variant={state.variant} className="shrink-0">
          {state.chip}
        </Badge>
        <span className="text-zinc-600">{state.line}</span>
        <span className="ml-auto flex gap-2">
          {canWithdraw && (
            <Button type="button" size="sm" variant="outline" loading={act.isPending} onClick={() => !act.isPending && act.mutate('withdraw')}>
              {forHead ? 'Withdraw for the head' : 'Withdraw'}
            </Button>
          )}
          {canSubmit && (
            <Button
              type="button"
              size="sm"
              onClick={() => {
                act.reset(); // a fresh attempt starts without the last one's refusal
                setConfirmSubmit(true);
              }}
            >
              {forHead ? `Submit ${department} for the head` : `Submit ${department}`}
            </Button>
          )}
        </span>
      </div>
      <AopReviewRow status={status} department={department} entry={entry} />
      <AopSyncRow status={status} department={department} entry={entry} />
      {act.error && !confirmSubmit && (
        <p role="alert" className="text-red-700">
          {(act.error as Error).message}
        </p>
      )}

      {confirmSubmit && (
        <Modal open onOpenChange={(open: boolean) => !open && setConfirmSubmit(false)}>
          <ModalContent className="!h-max !w-[min(100vw-2rem,30rem)] gap-4 rounded p-6">
            <ModalHeader>
              <ModalTitle className="!text-lg !font-semibold">
                Submit {department} for {current.name}?
              </ModalTitle>
            </ModalHeader>
            <p className="text-zinc-600">
              {forHead
                ? "You submit the department's drafts as they are now, in its head's place — the page and the log name you. "
                : "You submit the department's drafts as they are now, as its head. "}
              Karen, Patrick and Florian review them and Anna approves, which locks the department. Changing a draft before then
              withdraws the submission.
            </p>
            {act.error && (
              <p role="alert" className="text-sm text-red-700">
                {(act.error as Error).message}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setConfirmSubmit(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" loading={act.isPending} onClick={() => !act.isPending && act.mutate('submit')}>
                Submit
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

/** Name and open the next iteration. Opening it unlocks every department approved in the last one. */
function OpenIterationModal({
  onClose,
  onOpened,
  hasCurrent,
}: {
  onClose: () => void;
  onOpened: (result: Awaited<ReturnType<typeof openAopIteration>>) => void;
  hasCurrent: boolean;
}) {
  const [name, setName] = useState('');
  const open = useMutation({
    mutationFn: () => openAopIteration(name.trim()),
    onSuccess: (result) => {
      onOpened(result);
      onClose();
    },
  });
  const valid = name.trim().length > 0 && name.trim().length <= MAX_NAME;
  return (
    <Modal open onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-lg !font-semibold">Open {hasCurrent ? 'the next' : 'an'} iteration</ModalTitle>
        </ModalHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !open.isPending) open.mutate();
          }}
        >
          <Input
            label="Name"
            value={name}
            maxLength={MAX_NAME}
            placeholder="e.g. Iteration I"
            onChange={(event) => setName((event.target as HTMLInputElement).value)}
          />
          {hasCurrent && (
            <p className="text-sm text-zinc-600">
              Departments approved in the current iteration are unlocked, and every department submits again for this one.
              Roles set aside to revisit come back to their departments&apos; plans, with the reviewer&apos;s note.
            </p>
          )}
          {open.error && (
            <p role="alert" className="text-sm text-red-700">
              {(open.error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!valid} loading={open.isPending}>
              Open iteration
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}

/** The sentence after opening an iteration: how many set-aside roles came back, and which did not. */
function openedText({ returned, notReturned }: Awaited<ReturnType<typeof openAopIteration>>): string | null {
  if (notReturned === null) return 'Whether the roles set aside earlier came back could not be checked — see the Approvals tab.';
  const back = returned ? `${returned} ${returned === 1 ? 'role' : 'roles'} set aside earlier ${returned === 1 ? 'is' : 'are'} back in the plan to revisit.` : '';
  const stuck = notReturned.length
    ? ` These could not come back and stay set aside — restore them on the Approvals tab: ${notReturned.map((r) => r.title).join(', ')}.`
    : '';
  return back || stuck ? `${back}${stuck}`.trim() : null;
}

/**
 * The department's roles set aside to revisit (David, Oct 29 2026), for its head above all: those
 * back in the plan to rework per the reviewer's note, and those set aside in this iteration, which
 * come back when the next one opens. The note is shown to the head, the reviewers and Anna/David.
 */
function RevisitList({ entries }: { entries: NonNullable<AopIterationStatus['revisit']>[string] }) {
  if (!entries.length) return null;
  return (
    <div className="space-y-1 border-t border-zinc-100 pt-2 text-xs text-zinc-600">
      {/* Neutral: in the last iteration a set-aside role is declined, not revisited — its row says so. */}
      <p className="font-medium text-zinc-700">Set aside by the reviewers</p>
      <ul role="list" aria-label="Set aside by the reviewers" className="m-0 list-none space-y-0.5 p-0">
        {entries.map((r) => (
          <li key={r.recordId}>
            <span className="text-zinc-800">{r.title ?? 'A role'}</span>
            {r.back
              ? ` — back in the plan: set aside in ${r.iterationName ?? 'an earlier iteration'} by ${personName(r.by)}`
              : isFinalIteration(r.iterationName)
                ? ` — declined by ${personName(r.by)}`
                : ` — set aside by ${personName(r.by)}; comes back to the plan when the next iteration opens`}
            {r.note ? `: “${r.note}”` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}
